import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { useTheme } from "simplydo/react/theme";
import { Button, Header, Menu, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api, { websocketApi } from "api";
import { useAppDispatch, useAppSelector } from "store";

import CircularButtonWithNumber from "../CircularButtonWithNumber";
import TrayMessages from "./TrayMessages";
import TraySearch from "./TraySearch";
import type {
  MessageInputChangeEvent,
  MessageThreadWithAllUsers,
  MessageTraySearchState,
  NewMessageThread,
} from "./types";

type MessageTrayProps = {
  className?: string;
  isMobile?: boolean;
  style?: React.CSSProperties;
};

const MessageTray = ({ className, isMobile, style }: MessageTrayProps) => {
  const { t } = useTranslation();

  const user = useAppSelector((state) => state.user);
  const messageThreads = useAppSelector(
    (state) => state.messages.messageThreads,
  );
  const newMessageCount = useAppSelector(
    (state) => state.messages.newMessageCount,
  );

  const dispatch = useAppDispatch();

  const onMessageThreadSelected = useCallback(
    (
      thread: OpenAPI.Schemas["MessageThread"] | NewMessageThread | null,
      isPhone = false,
    ) => dispatch(actions.messages.selectThread(thread, isPhone)),
    [dispatch],
  );

  const onMessageThreadsReceived = useCallback(
    (threads: MessageThreadWithAllUsers[], receivedMessageCount: number) =>
      dispatch(actions.messages.receiveThreads(threads, receivedMessageCount)),
    [dispatch],
  );

  const theme = useTheme();
  const [searchState, setSearchState] = useState<MessageTraySearchState>({
    results: [],
    searching: false,
    searchValue: "",
  });
  const [popupOpen, setPopupOpen] = useState(false);
  const { results, searching, searchValue } = searchState;
  const [recipients, setRecipients] = useState<OpenAPI.Schemas["User"][]>([]);
  const userId = user?._id;

  const addRecipient = useCallback(
    (recipient: OpenAPI.Schemas["User"]) => {
      const isRecipient = recipients.find((r) => r._id === recipient._id);
      if (!isRecipient)
        setRecipients((prevRecipients) => [recipient, ...prevRecipients]);
    },
    [recipients],
  );

  const removeRecipient = useCallback(
    (recipient: OpenAPI.Schemas["User"]) =>
      setRecipients((prevRecipients) =>
        prevRecipients.filter((r) => r._id !== recipient._id),
      ),
    [],
  );

  const getThreads = useCallback(
    (data?: { thread?: string }) => {
      api.messages.getThreads(
        { page: 1, query: "" },
        ({ messageThreads: fetchedThreads, newMessageCount: fetchedCount }) => {
          onMessageThreadsReceived(fetchedThreads, fetchedCount);
          if (data?.thread) {
            const notifiedThread = fetchedThreads.find(
              (thread) => thread._id === data?.thread,
            );
            if (notifiedThread?.participantsMuted?.indexOf(userId ?? "") === -1)
              onMessageThreadSelected(notifiedThread, isMobile);
          }
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [userId, isMobile, onMessageThreadSelected, onMessageThreadsReceived],
  );

  useEffect(() => {
    if (!userId) return;
    getThreads();
    const messageSubscription = websocketApi.subscribe(
      `newMessage-${userId}`,
      (message) => {
        if (message.thread) {
          getThreads();
        }
      },
    );
    return () => messageSubscription.unsubscribe();
  }, [getThreads, userId]);

  useEffect(() => {
    if (popupOpen) {
      setSearchState((prevState) => ({
        ...prevState,
        results: [],
        searchValue: "",
      }));
      setRecipients([]);
      api.messages.getThreads(
        { page: 1, query: "" },
        ({ messageThreads: fetchedThreads, newMessageCount: fetchedCount }) => {
          onMessageThreadsReceived(fetchedThreads, fetchedCount);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    }
  }, [popupOpen, onMessageThreadsReceived]);

  const searchMessageRecipient = useThrottle(
    (e: MessageInputChangeEvent) => {
      api.search.messageRecipient(
        e?.target?.value || "",
        ({ users }) => {
          setSearchState((prevState) => ({
            ...prevState,
            searching: false,
            results: users,
          }));
        },
        (err) => {
          toast.error(err.message);
          setSearchState((prevState) => ({ ...prevState, searching: false }));
        },
      );
    },
    400,
    [setSearchState],
  );

  const handleSearchChange = useCallback(
    (e: MessageInputChangeEvent) => {
      e.persist();
      setSearchState((prevState) => ({
        ...prevState,
        searching: true,
        searchValue: e?.target?.value,
      }));
      searchMessageRecipient(e);
    },
    [searchMessageRecipient],
  );

  const selectRecipients = useCallback(
    (newUser: OpenAPI.Schemas["User"]) => {
      setPopupOpen(false);
      api.messages.findThreadByUser(
        newUser._id,
        (data) => {
          if (data?.thread) {
            onMessageThreadSelected(data.thread);
          } else if (user) {
            const newThread: NewMessageThread = {
              _id: `newThread:${newUser._id}`,
              participantUsers: [user, newUser],
            };
            onMessageThreadSelected(newThread, theme.sizes.isMobile);
          }
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [onMessageThreadSelected, user, theme.sizes.isMobile],
  );

  const selectMessageThread = useCallback(
    (thread: MessageThreadWithAllUsers) => {
      if (!userId) return;
      api.journey.record("users", userId, "openMessageMole");
      setPopupOpen(false);
      onMessageThreadSelected(thread, theme.sizes.isMobile);
    },
    [onMessageThreadSelected, theme.sizes.isMobile, userId],
  );

  const createChat = useCallback(
    (users: string[]) => {
      api.messages.createThread(
        { users },
        (thread) => {
          onMessageThreadSelected(thread);
          setPopupOpen(false);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [onMessageThreadSelected],
  );

  const renderMessageContent = useMemo(
    () => (
      <Menu
        borderless
        secondary
        vertical
        style={{ minWidth: theme.sizes.isMobile ? "100%" : 350 }}
      >
        <div style={{ padding: 5 }}>
          <Header style={{ marginBottom: 8 }}>Direct messages</Header>
          <Input
            size="small"
            fluid
            onChange={(e) => handleSearchChange(e)}
            placeholder={t("messages.findRecipient")}
            loading={searching}
            style={{ marginBottom: 8 }}
          />
          {recipients?.length || searchValue ? (
            <TraySearch
              results={results}
              searching={searching}
              recipients={recipients}
              selectRecipients={selectRecipients}
              addRecipient={addRecipient}
              removeRecipient={removeRecipient}
              createChat={createChat}
            />
          ) : (
            <>
              <TrayMessages
                messageThreads={messageThreads}
                selectMessageThread={selectMessageThread}
              />
              <Button fluid as={Link} to="/messages" style={{ marginTop: 10 }}>
                View all messages
              </Button>
            </>
          )}
        </div>
      </Menu>
    ),
    [
      theme,
      results,
      searching,
      recipients,
      selectRecipients,
      addRecipient,
      removeRecipient,
      createChat,
      messageThreads,
      selectMessageThread,
      handleSearchChange,
      searchValue,
      t,
    ],
  );

  if (isMobile) return renderMessageContent;
  return (
    <Popup
      on="click"
      style={{ padding: 10 }}
      positionFixed
      wide="very"
      position="bottom right"
      offset={[5, 0]}
      className="message-tray"
      open={popupOpen}
      onClose={() => setPopupOpen(false)}
      trigger={
        <div style={style}>
          <CircularButtonWithNumber
            className={className}
            onClick={() => setPopupOpen(!popupOpen)}
            icon="envelope"
            count={newMessageCount}
          />
        </div>
      }
      content={
        <div
          style={{
            minWidth: "350px",
            minHeight: "500px",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
          className="tray messages"
        >
          {renderMessageContent}
        </div>
      }
    />
  );
};

export default MessageTray;
